import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMsal } from "@azure/msal-react";
import { getAuthenticatedAccount } from "../utils/accountUtils";
import SearchField from "../components/SearchField";
import LoadingState from "../components/LoadingState";
import LoadMoreButton from "../components/LoadMoreButton";
import PageHeader from "../components/PageHeader";
import strings from "../strings";

const Database = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextLink, setNextLink] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { instance, accounts } = useMsal();
  const tenantId = process.env.REACT_APP_AUTH_TENENT_ID;

  const fetchRecords = useCallback(
    async (url = null) => {
      try {
        setLoading(true);
        const account = await getAuthenticatedAccount(
          instance,
          accounts,
          tenantId
        );
        const response = await instance.acquireTokenSilent({
          scopes: [process.env.REACT_APP_AUTH_SP_SCOPE],
          account: account,
        });

        const graphApiUrl = process.env.REACT_APP_GRAPH_API_URL;
        const siteId = process.env.REACT_APP_SP_SITE_ID;
        const listId = process.env.REACT_APP_ENVIRONMENTAL_DOCUMENT_DATABASE_ID;

        const apiUrl =
          url ||
          `${graphApiUrl}/sites/${siteId}/lists/${listId}/items?$expand=fields&$orderby=fields/field_0 desc&$top=10`;

        const result = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            "Content-Type": "application/json",
          },
        });

        const data = await result.json();
        setNextLink(data["@odata.nextLink"]);

        if (url) {
          setRecords((prev) => [...prev, ...data.value]);
        } else {
          setRecords(data.value);
        }
      } catch (error) {
        console.error("Error fetching records:", error);
      } finally {
        setLoading(false);
      }
    },
    [instance, accounts, tenantId]
  );

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleLoadMore = () => {
    if (nextLink) {
      fetchRecords(nextLink);
    }
  };

  const filteredRecords = records.filter((record) => {
    if (!record || !record.fields) return false;
    const searchLower = searchQuery.toLowerCase();
    const fields = record.fields;

    return (
      (fields.DOCID?.toString() || "").toLowerCase().includes(searchLower) ||
      (fields.Title1 || "").toLowerCase().includes(searchLower) ||
      (fields.CATEGORY || "").toLowerCase().includes(searchLower) ||
      (fields.PORTAREA || "").toLowerCase().includes(searchLower) ||
      (fields.DIVISION || "").toLowerCase().includes(searchLower) ||
      (fields.Sitename1 || "").toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <PageHeader 
          title={strings.database.pageHeader.title}
          externalLink={strings.database.pageHeader.externalLink}
        />

        <Box sx={{ mt: 4 }}>
          <SearchField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={strings.database.search.placeholder}
          />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.docId}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.title}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.date}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.category}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.portArea}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.division}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.fileFormat}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.siteName}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.created}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.modified}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.database.table.headers.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && records.length === 0 ? (
                  <LoadingState colSpan={11} />
                ) : filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      {strings.database.table.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((record) => {
                    const fields = record.fields || {};
                    return (
                      <TableRow key={record.id}>
                        <TableCell>
                          {fields.field_0 || fields.strDOCID || strings.common.na}
                        </TableCell>
                        <TableCell>
                          {fields.Title || fields.Title || strings.common.na}
                        </TableCell>
                        <TableCell>
                          {fields.field_3
                            ? new Date(fields.field_3).toLocaleDateString()
                            : strings.common.na}
                        </TableCell>
                        <TableCell>{fields.field_9 || strings.common.na}</TableCell>
                        <TableCell>{fields.field_11 || strings.common.na}</TableCell>
                        <TableCell>{fields.field_10 || strings.common.na}</TableCell>
                        <TableCell>{fields.field_12 || strings.common.na}</TableCell>
                        <TableCell>{fields.field_6 || strings.common.na}</TableCell>
                        <TableCell>
                          {record.createdDateTime
                            ? new Date(record.createdDateTime).toLocaleDateString()
                            : strings.common.na}
                        </TableCell>
                        <TableCell>
                          {record.lastModifiedDateTime
                            ? new Date(record.lastModifiedDateTime).toLocaleDateString()
                            : strings.common.na}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            href={record.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {nextLink && (
            <LoadMoreButton onClick={handleLoadMore} loading={loading} />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Database;
